import { formatIamScope, type IamScope, type IamScopeType } from '../../../services/iam';
import { scopeMeterLevel, scopeMeterTone, scopeWrapClass } from './scopeColor';

// Blast radius at a glance: WEIGHT (not just colour) marks platform/practice/
// patient so it still reads in grayscale. Colour tokens live in scopeColor.ts.

const SCOPE_LABEL: Record<IamScopeType, string> = {
    platform: 'Platform',
    practice: 'Practice',
    patient: 'Patient'
};

export function ScopeMeter({ level, tone }: { level: number; tone: 'dark' | 'light' }) {
    const lit = tone === 'dark' ? 'bg-white' : 'bg-current';
    const dim = tone === 'dark' ? 'bg-white/30' : 'bg-current opacity-25';
    return (
        <span className="inline-flex items-end gap-[2px]" aria-hidden="true">
            {[0, 1, 2].map((i) => (
                <span
                    key={i}
                    className={`w-[3px] rounded-sm ${i < level ? lit : dim}`}
                    style={{ height: `${(i + 1) * 3}px` }}
                />
            ))}
        </span>
    );
}

interface ScopeTagProps {
    scope: IamScope;
    /** Human name for the scope target (e.g. the practice's name) - shown in the title tooltip. */
    label?: string | null;
    size?: 'sm' | 'md';
    className?: string;
}

export function ScopeTag({ scope, label, size = 'md', className = '' }: ScopeTagProps) {
    const level = scopeMeterLevel(scope.type);
    const tone = scopeMeterTone(scope.type);
    const wrap = scopeWrapClass(scope.type);
    const padding =
        size === 'sm' ? 'px-1.5 py-0.5 text-[10px] gap-1' : 'px-2 py-1 text-[11px] gap-1.5';
    const title = label ? `${label} - ${formatIamScope(scope)}` : formatIamScope(scope);

    return (
        <span
            title={title}
            className={`inline-flex items-center rounded-md font-semibold whitespace-nowrap ${padding} ${wrap} ${className}`.trim()}
        >
            <ScopeMeter level={level} tone={tone} />
            <span>{SCOPE_LABEL[scope.type]}</span>
            {scope.id && <span className="font-mono opacity-90">:{scope.id}</span>}
        </span>
    );
}
